import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Title from '../../../components/Title';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { Badge, Button, Col, Form, ListGroup, Row } from 'react-bootstrap';
import Icon from '../../../components/Icon';
import OsSelect from '../../../components/forms/inputs/OsSelect';
import { useGetMotionModelsQuery } from '../../../api/motionModelApiSlice';
import useCustomTable from '../../../components/tables/useCustomTable';
import Block from '../../../components/Block';
import { Loader } from '../../../components/components';
import Table from '../../../components/tables/Table';
import styled from 'styled-components';
import { COLORS } from '../../../components/theme';
import { show as showLoader, hide as hideLoader } from '../../../components/loader/loaderSlice';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

const BlockDivider = styled.div`
  width: 100%;
  color: ${COLORS.primary};
  margin-bottom: 10px;
  margin-top: 10px;
  text-transform: uppercase;
`;

const Dropzone = ({mlFiles, setMlFiles, ...props}) => {
  const {required, name} = props; 
  const hiddenInputRef = useRef(null);
  const { t } = useTranslation();

  const onDrop = useCallback(acceptedFiles => {
    setMlFiles([...mlFiles, ...acceptedFiles])
  }, [setMlFiles])

  useEffect(() => {
    if (hiddenInputRef.current) {
      // Note the specific way we need to munge the file into the hidden input
      // https://stackoverflow.com/a/68182158/1068446
      const dataTransfer = new DataTransfer();
      mlFiles.forEach((v) => {
        dataTransfer.items.add(v);
      });
      hiddenInputRef.current.files = dataTransfer.files;
    }
  }, [mlFiles]);
  
  const {getRootProps, getInputProps} = useDropzone({
    maxFiles:1,
    onDrop: onDrop,
  });

  const humanFileSize = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  const removeFile = i => () => {
    const newFiles = [...mlFiles]
    newFiles.splice(i, 1)
    setMlFiles(newFiles)
  }

  const files = useMemo(() => {
    return mlFiles.map((file, i) => (
      <ListGroup.Item key={file.path}>
        {`${file.path} (${humanFileSize(file.size)})`}
        <span onClick={removeFile(i)} style={{cursor: 'pointer', marginLeft: '10px'}} className='float-right'>
          <Icon icon="trash" />
        </span>
      </ListGroup.Item>
    ))
  }, [mlFiles]);

  const dropzoneStyle = {
    border: `2px dashed ${COLORS.lightGrey}`,
    borderRadius: '5px',
    padding: '20px',
    textAlign: 'center',
    cursor: 'pointer',
  };
  

  return (
    <Row className="container">
      <Col {...getRootProps({className: 'dropzone'})} style={dropzoneStyle} as={Col} md={6}>
        {/*
          Add a hidden file input 
          Best to use opacity 0, so that the required validation message will appear on form submission
        */}

        <input type ="file" name={name} required={required} style ={{opacity: 0}} ref={hiddenInputRef}/>
        <input {...getInputProps()} />
        <p>{t('motion_model.dragndrop')}</p>
      </Col>
      <ListGroup as ={Col} md={6} className="pl-3">
        { files }
      </ListGroup>
    </Row>
  );
}

const ModelForm = () => {
  const { t } = useTranslation();
  const [os, setOs] = useState("");
  const dispatch = useDispatch();
  const { data: motionModels = {model_training_histories: []}, isSuccess, isLoading, refetch } = useGetMotionModelsQuery();
  const [mlFiles, setMlFiles] = useState([]);

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Now get the form data as you regularly would
    const form = e.currentTarget;
    const formData = new FormData(form);
    const csrfToken = document.querySelector('[name=csrf-token]');
    
    const reset = () => {
      form.reset();
      setOs("");
      setMlFiles([]);
    };
    
    dispatch(showLoader());
    try {
      const response = await fetch('/v1/motion_models', {
        method: 'POST',
        headers: { "X-CSRF-TOKEN": csrfToken.content },
        body: formData,
      });
      if (response.status === 422) {
        const data = await response.json();
        toast.error(data.error || "Validation error");
      } else if (!response.ok) {
        toast.error("An error occurred");
      } else {
        const data = await response.json();
        toast.success(data.message);
        reset();
      }
    } catch (error) {
      toast.error("Network error");
    } finally {
      dispatch(hideLoader());
      refetch();
    }
  }
  , [dispatch]);

  return (
    <Form onSubmit={onSubmit}>
      <Form.Group>
        <Form.Label>{t("motion_model.os")}</Form.Label>
        <OsSelect onChange={(e)=>(setOs(e))} name="os" value={os} required/>
      </Form.Group>
      <Form.Group controlId="training_at">
        <Form.Label>{t("motion_model.training_at")}</Form.Label>
        <Form.Control type="datetime-local" name="training_at" required />
      </Form.Group>
      <Form.Label>{t("motion_model.model_file")}</Form.Label>
      <Dropzone name="model_file" required mlFiles={mlFiles} setMlFiles={setMlFiles}/>
      <Form.Row className='d-flex justify-content-end mt-3'>
        <Button variant="primary" className='ml-auto' type="submit">
          {t("save")}
        </Button>
      </Form.Row>
    </Form>
  );
}

const ModelTable = ()=> {
  const { data: motionModels = {model_training_histories: []}, isSuccess, isLoading } = useGetMotionModelsQuery();
  const { model_training_histories: motionModelsList } = motionModels;

  const columns = [{
    Header: 'Key',
    accessor: 'key'
  },{
    Header: 'Os',
    accessor: 'os',
  },{
    Header: 'Training At',
    accessor: 'training_at',
    format: 'date'
  },{
    Header: 'Version',
    accessor: 'version',
  }];

  const tableOptions = {
    sortable: true,
    paginate: false, 
  };

  const {
    tableProps,
  } = useCustomTable(tableOptions)

  return (
    <Block className="mb-0">
      <Loader success={!isLoading && isSuccess} error={!isLoading && !isSuccess}>
        <Table
          data={motionModelsList}
          columns={columns}
          {...tableProps} />
      </Loader>
    </Block>
  )
}

const MotionModels = () => {
  const { t } = useTranslation();

  return (
    <>
      <Title main={t("motion_model.title")}/>

      <ModelTable />

      <BlockDivider></BlockDivider>

      <Block className="mb-0">
        <ModelForm />
      </Block>
    </>
  );
}

export default MotionModels;